/**
 * Lightweight cross-page "currently focused child" hint.
 *
 * Pages that have a child picker (ScreenTime, Timeline, Map) call
 * `useSetFocusedChild(id)` whenever their local selection changes. The
 * Ask-Kio drawer reads `useFocusedChild()` so it can pass `child_id` to the
 * server and show "Asking about <Name>" in its header, letting the LLM
 * resolve "how is she doing?" without needing the child's name in the
 * message.
 *
 * Reset to `null` when leaving a child-scoped page (handled by useEffect's
 * cleanup in the page that sets it).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface FocusValue {
  focusedChildId: string | null;
  setFocusedChildId: (id: string | null) => void;
}

const KidFocusContext = createContext<FocusValue | null>(null);

export function KidFocusProvider({ children }: { children: ReactNode }) {
  const [focusedChildId, setFocusedChildId] = useState<string | null>(null);
  const value = useMemo(
    () => ({ focusedChildId, setFocusedChildId }),
    [focusedChildId],
  );
  return <KidFocusContext.Provider value={value}>{children}</KidFocusContext.Provider>;
}

export function useFocusedChild(): string | null {
  const ctx = useContext(KidFocusContext);
  return ctx?.focusedChildId ?? null;
}

/**
 * Page hook: synchronise this page's currently-selected child with the
 * global focus. Clears the focus when the page unmounts.
 */
export function useSetFocusedChild(id: string | null): void {
  const ctx = useContext(KidFocusContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setFocusedChildId(id);
    return () => ctx.setFocusedChildId(null);
  }, [ctx, id]);
}
